import { tr } from '@/lib/i18n-core';
import { useEffect, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { MessageSquareText } from 'lucide-react';
import type { ArchiveEntityKind, ArchiveMessageDto, ArchivePairDto, ArchivePairPageDto } from '@his/shared';
import { get, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { cn, dateTime, humanize } from '@/lib/utils';
import { Sheet } from '@/components/ui/sheet';
import { Alert, Badge, Button, Spinner } from '@/components/ui/primitives';
import { JsonViewer } from './JsonViewer';

const PAGE_SIZE = 20;

type Side = 'request' | 'response';

function formatBytes(bytes: number): string {
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

export function pairTime(pair: ArchivePairDto): string | null {
  return pair.sentAt ?? pair.receivedAt;
}

function PairRow({ pair, selected, linked, onSelect }: { pair: ArchivePairDto; selected: boolean; linked: boolean; onSelect: () => void }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn('w-full rounded-md border p-2 text-start text-sm transition-colors focus-ring', selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/60')}
    >
      <span className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{humanize(pair.operation)}</span>
        {pair.status && <Badge variant="secondary">{humanize(pair.status)}</Badge>}
        {linked && <Badge variant="info">{tr('billing', 'nx_linked_pre_authorisation')}</Badge>}
        {!pair.response && <Badge variant="warning">{tr('billing', 'nx_no_response_yet')}</Badge>}
      </span>
      <span className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
        {pair.sentAt && (
          <span>
            {tr('billing', 'nx_request_sent')} {dateTime(pair.sentAt)}
          </span>
        )}
        {pair.receivedAt && (
          <span>
            {tr('billing', 'nx_response_received')} {dateTime(pair.receivedAt)}
          </span>
        )}
      </span>
    </button>
  );
}

function Detail({ pair }: { pair: ArchivePairDto }) {
  const [side, setSide] = useState<Side>(pair.request ? 'request' : 'response');
  useEffect(() => {
    setSide(pair.request ? 'request' : 'response');
  }, [pair.messageId, pair.request]);
  const message: ArchiveMessageDto | null = side === 'request' ? pair.request : pair.response;

  return (
    <div className="space-y-2 border-t pt-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant={side === 'request' ? 'default' : 'outline'} disabled={!pair.request} onClick={() => setSide('request')}>
          {tr('billing', 'nx_request')}
        </Button>
        <Button size="sm" variant={side === 'response' ? 'default' : 'outline'} disabled={!pair.response} onClick={() => setSide('response')}>
          {tr('billing', 'nx_response')}
        </Button>
        {message && (
          <span className="ms-auto text-xs text-muted-foreground">
            {dateTime(message.createdAt)} · {formatBytes(message.sizeBytes)}
          </span>
        )}
      </div>
      {!message ? (
        <Alert>{tr('billing', 'nx_no_response_yet')}</Alert>
      ) : !message.readable ? (
        <Alert variant="warning">{tr('billing', 'nx_message_not_readable')}</Alert>
      ) : (
        <JsonViewer body={message.body} rootLabel={side === 'request' ? tr('billing', 'nx_request') : tr('billing', 'nx_response')} />
      )}
    </div>
  );
}

export function MessagesDrawer({
  entityType,
  entityId,
  title,
  focusMessageId,
  onClose,
}: {
  entityType: ArchiveEntityKind;
  entityId: string;
  title: string;
  focusMessageId?: string | null;
  onClose: () => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(focusMessageId ?? null);
  const pairs = useInfiniteQuery({
    queryKey: ['nphies-archive-pairs', entityType, entityId],
    initialPageParam: '',
    queryFn: ({ pageParam }) => get<ArchivePairPageDto>(`/integrations/nphies/archive${qs({ entityType, entityId, view: 'pairs', limit: PAGE_SIZE, cursor: pageParam || undefined })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 60_000,
  });
  const items = pairs.data?.pages.flatMap((page) => page.items) ?? [];
  const selected = items.find((pair) => pair.messageId === selectedId) ?? (focusMessageId ? null : items[0]) ?? items[0] ?? null;

  return (
    <Sheet open onClose={onClose} title={title} description={tr('billing', 'nx_messages_description')} closeLabel={tr('billing', 'close')} className="sm:max-w-3xl">
      {pairs.isLoading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner /> {tr('billing', 'loading')}
        </p>
      ) : pairs.isError ? (
        <Alert variant="destructive">{describeError(pairs.error)}</Alert>
      ) : items.length === 0 ? (
        <Alert>{tr('billing', 'nx_no_messages')}</Alert>
      ) : (
        <div className="space-y-3">
          <div className="max-h-56 space-y-1.5 overflow-y-auto pe-1">
            {items.map((pair) => (
              <PairRow key={pair.messageId} pair={pair} selected={selected?.messageId === pair.messageId} linked={Boolean(pair.entityType) && pair.entityType !== entityType} onSelect={() => setSelectedId(pair.messageId)} />
            ))}
            {pairs.hasNextPage && (
              <Button size="sm" variant="outline" className="w-full" loading={pairs.isFetchingNextPage} onClick={() => void pairs.fetchNextPage()}>
                {tr('billing', 'nx_load_more')}
              </Button>
            )}
          </div>
          {selected && <Detail pair={selected} />}
        </div>
      )}
    </Sheet>
  );
}

export function MessagesButton({ entityType, entityId, title, focusMessageId }: { entityType: ArchiveEntityKind; entityId: string; title: string; focusMessageId?: string | null }) {
  const { can, hasModule } = useAuth();
  const [open, setOpen] = useState(false);
  if (!hasModule('nphies') || !can('nphies.archive.read')) return null;
  return (
    <>
      <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => setOpen(true)}>
        <MessageSquareText className="h-3.5 w-3.5" /> {tr('billing', 'nx_messages')}
      </Button>
      {open && <MessagesDrawer entityType={entityType} entityId={entityId} title={title} focusMessageId={focusMessageId} onClose={() => setOpen(false)} />}
    </>
  );
}
